import React, { useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Paper,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useAuth } from "@workos-inc/authkit-react";
import { downloadCsvTemplate, parseCsvData } from "../../../shared/utils/csvParser";
import { NewUser } from "../../../shared/utils/userValidation";
import { assignableStationRoles } from "../../../shared/constants/roleConstants";
import { profileClient } from "@/shared/api/clients";
import { apiErrorMessage } from "@/shared/api/apiError";
import { useOrgId } from "../../../shared/auth/orgContext";

export interface BulkUserUploadProps {
  onUsersUploaded: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  stationId?: string;
  csvUsers: NewUser[];
  setCsvUsers: React.Dispatch<React.SetStateAction<NewUser[]>>;
  resetPage: () => void;
}

interface BulkRowResult {
  row: number;
  email: string;
  code?: string;
  message?: string;
}

interface BulkResponse {
  createdUsers?: BulkRowResult[];
  errors?: BulkRowResult[];
  skipped?: BulkRowResult[];
}

interface UploadOutcome {
  total: number;
  createdCount: number;
  failed: BulkRowResult[];
  skippedCount: number;
  /** Transport-level failure message; the fate of in-flight rows is unknown. */
  interrupted: string | null;
}

/**
 * Rows per request. The backend invites rows sequentially (3-5 WorkOS calls
 * each) under API Gateway's 30s cap — large CSVs in one request time out
 * after partial creation with no accounting of what went through.
 */
const CHUNK_SIZE = 25;

/** Backend row numbers are chunk-relative and 1-based; make them absolute. */
function remapChunkRows(
  start: number,
  results: BulkRowResult[] | undefined,
): BulkRowResult[] {
  return (results ?? []).map((result) => ({
    ...result,
    row: start + result.row,
  }));
}

/**
 * One chunk's response as absolute-row result arrays, plus whether the seat
 * limit stopped the batch. The backend fills `skipped` only in that case, so
 * it doubles as the stop signal even if the error row's code changes shape.
 */
function buildBulkUserUploadResults(start: number, data: BulkResponse) {
  return {
    created: remapChunkRows(start, data.createdUsers),
    failed: remapChunkRows(start, data.errors),
    skipped: remapChunkRows(start, data.skipped),
    seatLimitReached:
      (data.errors ?? []).some((r) => r.code === "seat_limit_reached") ||
      (data.skipped ?? []).length > 0,
  };
}

/** Rows never sent because an earlier chunk hit the seat limit. */
function notAttemptedRows(users: NewUser[], firstRow: number): BulkRowResult[] {
  return users.map((user, index) => ({
    row: firstRow + index,
    email: user.email,
    code: "not_attempted",
    message: "Not attempted: station seat limit reached",
  }));
}
const FAILED_DISPLAY_LIMIT = 8;

const BulkUserUpload: React.FC<BulkUserUploadProps> = ({
  onUsersUploaded,
  onError,
  onSuccess,
  stationId,
  csvUsers,
  setCsvUsers,
  resetPage,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [uploadOutcome, setUploadOutcome] = useState<UploadOutcome | null>(null);

  const organizationId = useOrgId();
  const effectiveStationId = stationId ?? organizationId;
  const { role } = useAuth();
  // Mirrors the backend's assign rule (at or below the caller's own rank):
  // station admins and KARDA admins can both grant station_admin.
  const canGrantAdmin = assignableStationRoles(role ?? null).some(
    (assignable) => assignable.slug === "station_admin",
  );
  const adminRowCount = csvUsers.filter((user) => user.isAdmin).length;

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const processFile = (file: File) => {
    // The submit flow's row accounting is computed against the array captured
    // at submit time — swapping the file mid-flight would corrupt it.
    if (isSubmitting) {
      onError("Wait for the current upload to finish before loading another file.");
      return;
    }
    if (!file.name.toLowerCase().endsWith(".csv")) {
      onError("Please upload a CSV file");
      setCsvUsers([]);
      setCsvFile(null);
      setParseErrors([]);
      clearFileInput();
      return;
    }

    setCsvFile(file);
    setParseErrors([]);
    setUploadOutcome(null);
    const reader = new FileReader();
    reader.onerror = () => {
      setCsvFile(null);
      setCsvUsers([]);
      resetPage();
      clearFileInput();
      onError("Couldn't read the file. Re-save it and try uploading again.");
    };
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const result = parseCsvData(text);
      if (result.errors.length > 0) {
        setParseErrors(result.errors);
        setCsvUsers([]);
        resetPage();
        return;
      }

      setCsvUsers(result.users);
      resetPage();
      onSuccess(`Successfully parsed ${result.users.length} users from CSV`);
    };
    reader.readAsText(file);
  };

  const handleCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleSubmitCsvUsers = async () => {
    if (!effectiveStationId) {
      onError("Unable to submit: station or organization ID is missing. Please refresh and try again.");
      return;
    }

    if (csvUsers.length === 0) {
      onError("No parsed users to submit");
      return;
    }

    setIsSubmitting(true);
    setUploadOutcome(null);

    const total = csvUsers.length;
    const created: BulkRowResult[] = [];
    const failed: BulkRowResult[] = [];
    const skipped: BulkRowResult[] = [];
    let interrupted: string | null = null;

    try {
      for (let start = 0; start < csvUsers.length; start += CHUNK_SIZE) {
        const chunk = csvUsers.slice(start, start + CHUNK_SIZE);
        try {
          const { data } = await profileClient.post<BulkResponse>(
            `/v1/stations/${effectiveStationId}/users/bulk`,
            {
              rows: chunk.map((user) => ({
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                isAdmin: !!user.isAdmin,
                metadata: {
                  division_name: user.department,
                  job_title: user.position,
                  badge_number: user.badgeNumber,
                },
              })),
            },
          );

          const results = buildBulkUserUploadResults(start, data);
          created.push(...results.created);
          failed.push(...results.failed);
          skipped.push(...results.skipped);

          if (results.seatLimitReached) {
            // Later chunks would hit the same limit — account for them here.
            skipped.push(
              ...notAttemptedRows(
                csvUsers.slice(start + chunk.length),
                start + chunk.length + 1,
              ),
            );
            break;
          }
        } catch (error) {
          interrupted = apiErrorMessage(
            error,
            "The upload was interrupted before all rows were processed.",
          );
          break;
        }
      }
    } finally {
      setIsSubmitting(false);
    }

    if (created.length === total) {
      onSuccess(
        total === 1 ? "Invitation sent to 1 user." : `Invitations sent to all ${total} users.`,
      );
      setCsvUsers([]);
      setCsvFile(null);
      resetPage();
      clearFileInput();
      onUsersUploaded();
      return;
    }

    // Partial (or zero) success: keep the rows that didn't go through loaded
    // so the admin can fix the cause and resubmit only those. Filter the
    // captured array — row numbers were computed against it.
    const createdRows = new Set(created.map((result) => result.row));
    setCsvUsers(csvUsers.filter((_, index) => !createdRows.has(index + 1)));
    resetPage();

    setUploadOutcome({
      total,
      createdCount: created.length,
      failed,
      skippedCount: skipped.length,
      interrupted,
    });
    onError(
      `${created.length} of ${total} invitations sent — see the upload results below.`,
    );
    if (created.length > 0) {
      onUsersUploaded();
    }
  };

  const handleRemoveCsvFile = () => {
    setCsvFile(null);
    setCsvUsers([]);
    setParseErrors([]);
    setUploadOutcome(null);
    resetPage();
    clearFileInput();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[400]}`,
        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.secondary.main, 0.1),
            color: "secondary.main",
          }}
        >
          <CloudUploadIcon />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Bulk Invite Users
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Import multiple users at once
          </Typography>
        </Box>
      </Box>

      <Alert
        severity="info"
        icon={<WarningIcon fontSize="small" />}
        sx={{
          mb: 3,
          borderRadius: 2,
          "& .MuiAlert-message": { fontSize: "0.85rem" },
        }}
      >
        Upload a CSV file with the required columns. Download the template below
        for the correct format.
      </Alert>

      <input
        ref={fileInputRef}
        id="csv-upload"
        type="file"
        accept=".csv"
        disabled={isSubmitting}
        onChange={handleCsvUpload}
        style={{ display: "none" }}
      />

      <label htmlFor="csv-upload">
        <Box
          onDragOver={(event) => {
            event.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setIsDragOver(false);
            const file = event.dataTransfer.files?.[0];
            if (file) processFile(file);
          }}
          sx={{
            border: `2px dashed ${
              csvFile
                ? theme.palette.success.main
                : isDragOver
                  ? theme.palette.primary.main
                  : alpha(theme.palette.primary.main, 0.3)
            }`,
            borderRadius: 3,
            p: 4,
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            bgcolor: csvFile
              ? alpha(theme.palette.success.main, 0.05)
              : alpha(theme.palette.primary.main, isDragOver ? 0.08 : 0.02),
            "&:hover": {
              borderColor: csvFile
                ? theme.palette.success.main
                : theme.palette.primary.main,
              bgcolor: csvFile
                ? alpha(theme.palette.success.main, 0.08)
                : alpha(theme.palette.primary.main, 0.05),
            },
            mb: 3,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 180,
          }}
        >
          {csvFile ? (
            <>
              <CheckCircleIcon
                sx={{ fontSize: 48, color: "success.main", mb: 1.5 }}
              />
              <Typography variant="body1" fontWeight={600} gutterBottom>
                {csvFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click to choose a different file
              </Typography>
            </>
          ) : (
            <>
              <CloudUploadIcon
                sx={{
                  fontSize: 48,
                  color: alpha(theme.palette.primary.main, 0.4),
                  mb: 1.5,
                }}
              />
              <Typography variant="body1" fontWeight={500} gutterBottom>
                Click to upload CSV file
              </Typography>
              <Typography variant="caption" color="text.secondary">
                or drag and drop your file here
              </Typography>
            </>
          )}
        </Box>
      </label>

      {parseErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle>This file can't be submitted yet</AlertTitle>
          <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
            {parseErrors.slice(0, FAILED_DISPLAY_LIMIT).map((error) => (
              <li key={error}>{error}</li>
            ))}
          </Box>
          {parseErrors.length > FAILED_DISPLAY_LIMIT && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              …and {parseErrors.length - FAILED_DISPLAY_LIMIT} more. Fix the CSV
              and upload it again.
            </Typography>
          )}
        </Alert>
      )}

      {!canGrantAdmin && adminRowCount > 0 && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          {adminRowCount === 1
            ? "1 row has is_admin set"
            : `${adminRowCount} rows have is_admin set`}
          , but your account can't grant admin privileges — those rows will
          fail. Set is_admin to false, or ask a station or KARDA administrator
          to grant admin access after the users are added.
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={downloadCsvTemplate}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
            px: 2.5,
          }}
        >
          Download Template
        </Button>

        {csvFile && (
          <>
            <Button
              variant="outlined"
              color="error"
              onClick={handleRemoveCsvFile}
              disabled={isSubmitting}
              startIcon={<CancelIcon />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                px: 2.5,
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                },
              }}
            >
              Remove File
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitCsvUsers}
              disabled={isSubmitting || !effectiveStationId || csvUsers.length === 0}
              startIcon={<UploadIcon />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                boxShadow: `0 4px 14px ${alpha(
                  theme.palette.primary.main,
                  0.3
                )}`,
                "&:hover": {
                  boxShadow: `0 6px 20px ${alpha(
                    theme.palette.primary.main,
                    0.4
                  )}`,
                },
              }}
            >
              {isSubmitting ? "Uploading..." : "Upload CSV File"}
            </Button>
          </>
        )}
      </Box>

      {uploadOutcome && (
        <Alert
          severity="error"
          onClose={() => setUploadOutcome(null)}
          sx={{ mt: 3, borderRadius: 2, "& .MuiAlert-message": { width: "100%" } }}
        >
          <AlertTitle>
            Upload results: {uploadOutcome.createdCount} of {uploadOutcome.total}{" "}
            invitations sent
          </AlertTitle>
          {uploadOutcome.interrupted && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              {uploadOutcome.interrupted} Refresh the user list to confirm what
              went through before re-submitting.
            </Typography>
          )}
          {uploadOutcome.failed.length > 0 && (
            <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
              {uploadOutcome.failed
                .slice(0, FAILED_DISPLAY_LIMIT)
                .map((failure) => (
                  <li key={`${failure.row}-${failure.email}`}>
                    <strong>{failure.email}</strong>:{" "}
                    {failure.message ?? "Failed"}
                  </li>
                ))}
            </Box>
          )}
          {uploadOutcome.failed.length > FAILED_DISPLAY_LIMIT && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              …and {uploadOutcome.failed.length - FAILED_DISPLAY_LIMIT} more
              failures.
            </Typography>
          )}
          {uploadOutcome.skippedCount > 0 && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {uploadOutcome.skippedCount === 1
                ? "1 user wasn't attempted"
                : `${uploadOutcome.skippedCount} users weren't attempted`}{" "}
              because the station's seat limit was reached. They're still
              loaded in the preview — free up seats (or raise the limit) and
              upload again.
            </Typography>
          )}
        </Alert>
      )}
    </Paper>
  );
};

export default BulkUserUpload;
